import { useEffect, useState } from "react";
import styled from "styled-components";
import { fetchWrapper } from "../../utils/apiHandlers";
import SmallButton from "../SmallButton";
import LoadingComponent from "../LoadingComponent";
import theme from "../../theme";

const fmtUptime = (ms: number) => {
  if (!ms) return "—"
  let mins = Math.floor(ms / 60000)
  if (mins < 60) return `${mins}m`
  let hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ${mins % 60}m`
  return `${Math.floor(hrs / 24)}d ${hrs % 24}h`
}

const STATUS_DOT: { [key: string]: string } = { online: "#2E9E5B", stopped: "#5B6472", errored: "#FF5578", unknown: "#DDB13A" }

const SystemComp: React.FC = () => {
  const [data, setdata] = useState<any>(null)
  const [loading, setloading] = useState(false)

  const load = async () => {
    setloading(true)
    try {
      let response = await fetchWrapper.post("/api/admin/systemstatus", {})
      setdata(response)
    } catch (error) { }
    setloading(false)
  }

  useEffect(() => { load() }, [])

  return (
    <MainContainer>
      <TopRow>
        <Title>System</Title>
        <SmallButton text={loading ? "Loading…" : "Refresh"} handleClick={load} />
      </TopRow>
      {loading && !data && <LoadingComponent />}

      {data && (
        <>
          <Cards>
            <Card>
              <CardHead>
                <Dot style={{ background: STATUS_DOT[data.processes?.chatServer?.status] || STATUS_DOT.unknown }} />
                <CardLabel>CHAT SERVER</CardLabel>
              </CardHead>
              <CardValue>{data.processes?.chatServer?.status || "unknown"}</CardValue>
              <CardSub>up {fmtUptime(data.processes?.chatServer?.uptimeMs)} · {data.processes?.chatServer?.restarts ?? "?"} restarts</CardSub>
            </Card>

            <Card>
              <CardHead>
                <Dot style={{ background: data.chatServerReachable ? STATUS_DOT.online : STATUS_DOT.errored }} />
                <CardLabel>CHAT SERVER REACHABILITY</CardLabel>
              </CardHead>
              <CardValue>{data.chatServerReachable ? "Reachable" : "Unreachable"}</CardValue>
              <CardSub>{data.chatServerReachable ? `responded in ${data.chatServerMs}ms` : "check pm2 / chatbotip.txt"}</CardSub>
            </Card>

            <Card>
              <CardHead>
                <Dot style={{ background: STATUS_DOT[data.processes?.express?.status] || STATUS_DOT.unknown }} />
                <CardLabel>EXPRESS (miamigo-express-dev)</CardLabel>
              </CardHead>
              <CardValue>{data.processes?.express?.status || "unknown"}</CardValue>
              <CardSub>up {fmtUptime(data.processes?.express?.uptimeMs)} · {data.processes?.express?.restarts ?? "?"} restarts</CardSub>
            </Card>
          </Cards>

          <Columns>
            <Col>
              <SectionTitle>Crawl queue</SectionTitle>
              <QueueCard>
                {Object.entries(data.queue || {}).length ? Object.entries(data.queue).map(([status, count]: any) => (
                  <QueueRow key={status}>
                    <span>{status}</span>
                    <b>{count}</b>
                  </QueueRow>
                )) : <Muted>No businesses in the directory yet.</Muted>}
              </QueueCard>
            </Col>

            <Col style={{ flex: 1.6 }}>
              <SectionTitle>Recent crawl errors</SectionTitle>
              {data.recentErrors?.length ? (
                <QueueCard>
                  {data.recentErrors.map((e: any) => (
                    <ErrorRow key={e._id}>
                      <span>{e.name}</span>
                      <Muted>{(e.crawlerror || '').slice(0, 60)}</Muted>
                    </ErrorRow>
                  ))}
                </QueueCard>
              ) : <QueueCard><Muted>No recent crawl errors.</Muted></QueueCard>}
            </Col>
          </Columns>
        </>
      )}
    </MainContainer>
  );
};

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: ${theme.colors.text};
`

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div`
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: 14px;
`

const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 999px;
`

const CardLabel = styled.div`
  font-size: 10px;
  font-weight: 800;
  color: ${theme.colors.textMuted};
`

const CardValue = styled.div`
  font-size: 18px;
  font-weight: 800;
  margin-top: 4px;
  text-transform: capitalize;
`

const CardSub = styled.div`
  font-size: 11px;
  color: ${theme.colors.textMuted};
`

const Columns = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  @media (max-width: 900px) {
    flex-direction: column;
  }
`

const Col = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const SectionTitle = styled.div`
  font-weight: 800;
  font-size: 13px;
`

const QueueCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: 4px 12px;
`

const QueueRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 12px;
  border-bottom: 1px solid ${theme.colors.border};
  text-transform: capitalize;
  &:last-child { border-bottom: none; }
`

const ErrorRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
  font-size: 12px;
  border-bottom: 1px solid ${theme.colors.border};
  &:last-child { border-bottom: none; }
`

const Muted = styled.div`
  font-size: 11px;
  color: ${theme.colors.textMuted};
  padding: 8px 0;
`

export default SystemComp;
